import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { asList, budgetLineAmount, costCodeLabel, costTypeLabel, getJson, loadCostLookups, money, shortDate, statusClass } from '../helpers';

export default function RecordDetailPage({ parentLabel, parentTo, loader }) {
  const { projectVuid } = useParams();
  const [projectName, setProjectName] = useState('');
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    Promise.all([
      loader(),
      projectVuid ? getJson(`/api/projects/${projectVuid}`).catch(() => null) : Promise.resolve(null),
    ])
      .then(([data, project]) => {
        if (cancelled) return;
        setRecord(data);
        if (project?.project_name) setProjectName(project.project_name);
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || 'Unable to load this record');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectVuid, parentTo]);

  if (loading) {
    return <div className="pc-page"><div className="pc-empty">Loading record…</div></div>;
  }
  if (error || !record) {
    return <div className="pc-page"><div className="pc-empty">{error || 'Record not found'}</div></div>;
  }

  return (
    <div className="pc-page">
      <div className="pc-crumb">
        {projectVuid ? (
          <>
            <Link to="/procore">Portfolio</Link>
            {' / '}
            <Link to={`/procore/projects/${projectVuid}`}>{projectName || 'Project'}</Link>
            {' / '}
            <Link to={parentTo}>{parentLabel}</Link>
            {' / '}
            {record.title}
          </>
        ) : (
          <>
            <Link to={parentTo}>{parentLabel}</Link>
            {' / '}
            {record.title}
          </>
        )}
      </div>
      <div className="pc-title-row">
        <h1>{record.title}</h1>
        {record.status ? (
          <span className={`pc-pill ${statusClass(record.status)}`}>{record.status}</span>
        ) : null}
      </div>
      <div className="pc-card">
        {record.fields?.length ? (
          <div className="pc-kv">
            {record.fields.map((field) => (
              <div key={field.label}>
                <div className="pc-label">{field.label}</div>
                <div className="pc-value">
                  {field.status ? (
                    <span className={`pc-pill ${statusClass(field.value)}`}>{field.value || '—'}</span>
                  ) : field.money ? (
                    money(field.value)
                  ) : field.date ? (
                    shortDate(field.value)
                  ) : (
                    field.value || '—'
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : null}
        <div className="pc-panel-h">{record.lineTitle || 'Line items'}</div>
        {!record.lines?.length ? (
          <div className="pc-empty">No line items on this record.</div>
        ) : (
          <table className="pc-table">
            <thead>
              <tr>
                {(record.lineColumns || []).map((column) => (
                  <th key={column.key} className={column.money ? 'pc-right' : undefined}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {record.lines.map((line, index) => (
                <tr key={line.id || index}>
                  {(record.lineColumns || []).map((column) => (
                    <td key={column.key} className={column.money ? 'pc-right' : undefined}>
                      {column.money
                        ? money(line[column.key])
                        : column.date
                          ? shortDate(line[column.key])
                          : line[column.key] || '—'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export function CommitmentDetail() {
  const { projectVuid, recordVuid } = useParams();
  return (
    <RecordDetailPage
      parentLabel="Commitments"
      parentTo={`/procore/projects/${projectVuid}/commitments`}
      loader={async () => {
        const data = await getJson(`/api/commitments/${recordVuid}`);
        const lines = data.line_items || data.commitment_lines || [];
        return {
          title: `${data.commitment_number || ''} ${data.commitment_name || 'Commitment'}`.trim(),
          status: data.status,
          fields: [
            { label: 'Number', value: data.commitment_number },
            { label: 'Vendor', value: data.vendor?.vendor_name || data.vendor_name },
            { label: 'Original Amount', value: data.original_amount, money: true },
            { label: 'Change Orders', value: data.change_orders_amount, money: true },
            { label: 'Current Amount', value: data.commitment_amount, money: true },
            { label: 'Retainage', value: data.retainage_percentage != null ? `${data.retainage_percentage}%` : '—' },
            { label: 'Date', value: data.commitment_date, date: true },
          ],
          lineTitle: 'Schedule of Values',
          lineColumns: [
            { key: 'description', label: 'Description' },
            { key: 'change_order_number', label: 'CCO' },
            { key: 'quantity', label: 'Qty' },
            { key: 'unit_cost', label: 'Unit Cost', money: true },
            { key: 'total_cost', label: 'Amount', money: true },
          ],
          lines: lines.map((line) => ({
            id: line.vuid,
            description: line.description,
            change_order_number: line.change_order_number,
            quantity: line.quantity,
            unit_cost: line.unit_cost,
            total_cost: line.total_cost,
          })),
        };
      }}
    />
  );
}

export function ApInvoiceDetail() {
  const { projectVuid, recordVuid } = useParams();
  return (
    <RecordDetailPage
      parentLabel="Invoicing"
      parentTo={`/procore/projects/${projectVuid}/invoicing`}
      loader={async () => {
        const [invoice, linePayload] = await Promise.all([
          getJson(`/api/ap-invoices/${recordVuid}`),
          getJson(`/api/ap-invoices/${recordVuid}/line-items`).catch(() => []),
        ]);
        const lines = asList(linePayload).length ? asList(linePayload) : (invoice.line_items || []);
        return {
          title: invoice.invoice_number || 'AP Invoice',
          status: invoice.status,
          fields: [
            { label: 'Vendor', value: invoice.vendor_name || invoice.vendor?.vendor_name },
            { label: 'Invoice Date', value: invoice.invoice_date, date: true },
            { label: 'Due Date', value: invoice.due_date, date: true },
            { label: 'Subtotal', value: invoice.subtotal, money: true },
            { label: 'Retention Held', value: invoice.retention_held, money: true },
            { label: 'Total', value: invoice.total_amount, money: true },
          ],
          lineTitle: 'Invoice lines',
          lineColumns: [
            { key: 'description', label: 'Description' },
            { key: 'quantity', label: 'Qty' },
            { key: 'unit_cost', label: 'Unit Cost', money: true },
            { key: 'total_amount', label: 'Amount', money: true },
          ],
          lines: lines.map((line) => ({
            id: line.vuid,
            description: line.description,
            quantity: line.quantity,
            unit_cost: line.unit_cost || line.unit_price,
            total_amount: line.total_amount || line.total_cost || line.amount,
          })),
        };
      }}
    />
  );
}

export function OwnerBillingDetail() {
  const { projectVuid, recordVuid } = useParams();
  return (
    <RecordDetailPage
      parentLabel="Invoicing"
      parentTo={`/procore/projects/${projectVuid}/invoicing`}
      loader={async () => {
        const [billing, linePayload] = await Promise.all([
          getJson(`/api/project-billings/${recordVuid}`),
          getJson(`/api/project-billings/${recordVuid}/line-items`).catch(() => []),
        ]);
        const lines = asList(linePayload).length ? asList(linePayload) : (billing.line_items || []);
        return {
          title: billing.billing_number || billing.application_number || 'Owner Billing',
          status: billing.status,
          fields: [
            { label: 'Period', value: billing.accounting_period?.name || billing.period_name },
            { label: 'Billing Date', value: billing.billing_date || billing.application_date, date: true },
            { label: 'Total', value: billing.total_amount || billing.current_payment_due, money: true },
          ],
          lineTitle: 'SOV lines',
          lineColumns: [
            { key: 'description', label: 'Description' },
            { key: 'this_period', label: 'This Period', money: true },
            { key: 'total_completed', label: 'Completed', money: true },
            { key: 'retainage', label: 'Retainage', money: true },
          ],
          lines: lines.map((line) => ({
            id: line.vuid,
            description: line.description,
            this_period: line.work_completed_this_period || line.this_period_amount,
            total_completed: line.total_completed || line.work_completed_to_date,
            retainage: line.retainage_held || line.retainage_amount,
          })),
        };
      }}
    />
  );
}

export function ChangeOrderDetail() {
  const { projectVuid, kind, recordVuid } = useParams();
  const parentTo = `/procore/projects/${projectVuid}/change-orders`;
  if (kind === 'commitment') {
    return <CommitmentChangeOrderDetail />;
  }
  return (
    <RecordDetailPage
      parentLabel="Change Orders"
      parentTo={parentTo}
      loader={async () => {
        const listPath = kind === 'internal' ? '/api/internal-change-orders' : '/api/external-change-orders';
        const linePath = kind === 'internal'
          ? `/api/internal-change-orders/${recordVuid}/lines`
          : `/api/external-change-orders/${recordVuid}/lines`;
        const [list, lines] = await Promise.all([
          getJson(listPath),
          getJson(linePath).catch(() => []),
        ]);
        const header = asList(list).find((row) => row.vuid === recordVuid) || {};
        return {
          title: `${header.change_order_number || ''} ${header.description || header.change_order_name || 'Change Order'}`.trim(),
          status: header.status,
          fields: [
            { label: 'Type', value: kind === 'internal' ? 'Internal' : 'Prime / External' },
            { label: 'Number', value: header.change_order_number },
            { label: 'Amount', value: header.total_amount, money: true },
            { label: 'Date', value: header.change_order_date || header.approval_date, date: true },
          ],
          lineColumns: [
            { key: 'description', label: 'Description' },
            { key: 'cost_code', label: 'Cost Code' },
            { key: 'amount', label: 'Amount', money: true },
          ],
          lines: asList(lines).map((line) => ({
            id: line.vuid,
            description: line.description,
            cost_code: line.cost_code || line.cost_code_code,
            amount: line.total_amount || line.contract_amount_change || line.amount,
          })),
        };
      }}
    />
  );
}

export function CommitmentChangeOrderDetail() {
  const { projectVuid, recordVuid, coNumber } = useParams();
  const number = decodeURIComponent(coNumber || '');
  return (
    <RecordDetailPage
      parentLabel="Change Orders"
      parentTo={`/procore/projects/${projectVuid}/change-orders`}
      loader={async () => {
        const data = await getJson(`/api/commitments/${recordVuid}`);
        const lines = (data.line_items || []).filter(
          (line) => String(line.change_order_number || '') === number
        );
        const amount = lines.reduce((sum, line) => sum + Number(line.total_cost || 0), 0);
        return {
          title: `CCO ${number}`,
          status: data.status,
          fields: [
            { label: 'Type', value: 'Commitment Change Order' },
            { label: 'Commitment', value: `${data.commitment_number} ${data.commitment_name || ''}`.trim() },
            { label: 'Vendor', value: data.vendor?.vendor_name },
            { label: 'Amount', value: amount, money: true },
          ],
          lineColumns: [
            { key: 'description', label: 'Description' },
            { key: 'quantity', label: 'Qty' },
            { key: 'unit_cost', label: 'Unit Cost', money: true },
            { key: 'total_cost', label: 'Amount', money: true },
          ],
          lines: lines.map((line) => ({
            id: line.vuid,
            description: line.description,
            quantity: line.quantity,
            unit_cost: line.unit_cost,
            total_cost: line.total_cost,
          })),
        };
      }}
    />
  );
}

export function DirectCostDetail() {
  const { projectVuid, recordVuid } = useParams();
  return (
    <RecordDetailPage
      parentLabel="Direct Costs"
      parentTo={`/procore/projects/${projectVuid}/direct-costs`}
      loader={async () => {
        const data = await getJson(`/api/project-expenses/${recordVuid}`);
        return {
          title: data.expense_number || data.description || 'Direct Cost',
          status: data.status,
          fields: [
            { label: 'Vendor', value: data.vendor?.vendor_name },
            { label: 'Date', value: data.expense_date, date: true },
            { label: 'Amount', value: data.amount, money: true },
            { label: 'Cost Code', value: data.cost_code?.code },
            { label: 'Cost Type', value: data.cost_type?.cost_type || data.cost_type?.abbreviation },
            { label: 'Memo', value: data.memo || data.description },
          ],
          lineTitle: 'Details',
          lineColumns: [
            { key: 'description', label: 'Description' },
            { key: 'amount', label: 'Amount', money: true },
          ],
          lines: [
            {
              id: data.vuid,
              description: data.description || data.memo || 'Direct cost',
              amount: data.amount,
            },
          ],
        };
      }}
    />
  );
}

export function BudgetDetail() {
  const { projectVuid, recordVuid } = useParams();
  return (
    <RecordDetailPage
      parentLabel="Budget"
      parentTo={`/procore/projects/${projectVuid}/budget`}
      loader={async () => {
        const [{ codeMap, typeMap }, budget, linePayload] = await Promise.all([
          loadCostLookups(projectVuid),
          getJson(`/api/project-budgets/${recordVuid}`),
          getJson(`/api/project-budgets/${recordVuid}/lines`).catch(() => []),
        ]);
        const lines = asList(linePayload).length ? asList(linePayload) : (budget.budget_lines || budget.lines || []);
        return {
          title: budget.description || budget.budget_type || 'Budget',
          status: budget.status,
          fields: [
            { label: 'Type', value: budget.budget_type },
            { label: 'Date', value: budget.budget_date, date: true },
            { label: 'Total', value: budget.budget_amount, money: true },
            { label: 'Finalized', value: budget.finalized ? 'Yes' : 'No' },
          ],
          lineColumns: [
            { key: 'cost_code', label: 'Cost Code' },
            { key: 'cost_type', label: 'Cost Type' },
            { key: 'amount', label: 'Original Budget', money: true },
            { key: 'notes', label: 'Notes' },
          ],
          lines: lines.map((line) => ({
            id: line.vuid,
            cost_code: costCodeLabel(line, codeMap),
            cost_type: costTypeLabel(line, typeMap),
            amount: budgetLineAmount(line),
            notes: line.notes,
          })),
        };
      }}
    />
  );
}

export function DirectoryDetail() {
  const { kind, recordVuid } = useParams();
  const isVendor = kind === 'vendors';
  return (
    <RecordDetailPage
      parentLabel="Directory"
      parentTo="/procore/directory"
      loader={async () => {
        const data = await getJson(isVendor ? `/api/vendors/${recordVuid}` : `/api/employees/${recordVuid}`);
        const name = isVendor
          ? data.vendor_name || data.company_name
          : data.employee_name || `${data.first_name || ''} ${data.last_name || ''}`.trim();
        return {
          title: name || 'Directory record',
          status: data.status,
          fields: isVendor
            ? [
                { label: 'Number', value: data.vendor_number },
                { label: 'Company', value: data.company_name },
                { label: 'Email', value: data.email },
                { label: 'Phone', value: data.phone },
              ]
            : [
                { label: 'Number', value: data.employee_number },
                { label: 'Email', value: data.email },
                { label: 'Title', value: data.job_title || data.title },
              ],
          lineTitle: 'Contact',
          lineColumns: [
            { key: 'label', label: 'Field' },
            { key: 'value', label: 'Value' },
          ],
          lines: [
            { id: '1', label: 'Name', value: name },
            { id: '2', label: 'Status', value: data.status },
          ],
        };
      }}
    />
  );
}
